'use client';

import { useEffect, useState } from 'react';
import styles from './TableOfContents.module.css';

type TocItem = {
    level: number;
    text: string;
    slug: string;
};

export default function TableOfContents({ toc }: { toc: TocItem[] }) {
    const [activeSlug, setActiveSlug] = useState<string>('');
    const [open, setOpen] = useState(false);

    useEffect(() => {
        if (!toc.length) return;

        const headings = toc
            .map((item) => document.getElementById(item.slug))
            .filter((el): el is HTMLElement => Boolean(el));

        if (!headings.length) return;

        const observer = new IntersectionObserver(
            (entries) => {
                const visible = entries
                    .filter((entry) => entry.isIntersecting)
                    .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);

                if (visible.length > 0) {
                    setActiveSlug(visible[0].target.id);
                }
            },
            { rootMargin: '-100px 0px -70% 0px', threshold: 0 }
        );

        headings.forEach((el) => observer.observe(el));
        return () => observer.disconnect();
    }, [toc]);

    if (!toc.length) return null;

    const activeText = toc.find((item) => item.slug === activeSlug)?.text;

    return (
        <>
            <button
                type="button"
                className={styles.mobileToggle}
                aria-expanded={open}
                onClick={() => setOpen((v) => !v)}
            >
                <span>{activeText ? `Contents · ${activeText}` : 'Contents'}</span>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="m6 9 6 6 6-6" />
                </svg>
            </button>

            <ol className={open ? styles.listOpen : undefined}>
                {toc.map((item) => (
                    <li key={item.slug} style={{ paddingLeft: Math.max(0, item.level - 2) * 12 }}>
                        <a
                            href={`#${item.slug}`}
                            className={item.slug === activeSlug ? 'is-active' : undefined}
                            onClick={() => setOpen(false)}
                        >
                            {item.text}
                        </a>
                    </li>
                ))}
            </ol>
        </>
    );
}
